/**
 * Read model for the add-players panel's Suggested tab (SE-5490, consuming the
 * SE-5523 recommendation API).
 *
 * Two deliberate non-behaviours:
 *
 * 1. **No client-side eligibility filtering.** The backend already scopes
 *    candidates to the board's pipeline motion and already excludes players
 *    carded in THIS pipeline. Re-deriving either here would silently diverge
 *    from the server the moment the rules change, so this hook renders what it
 *    receives.
 * 2. **No mutation on dismiss.** Dismissal is a local, session/pipeline-scoped
 *    hide — see `stores/dismissedSuggestionsStore` for why routing it through
 *    the org-wide removal mechanism would be a data-loss bug.
 *
 * The one transform applied is ORDER: `bestRank` (1 = strongest) is the primary
 * sort key and is applied client-side so the panel's order is guaranteed
 * regardless of the order the connection happens to arrive in.
 * @module features/player-kanban/hooks/useSuggestedPlayers
 */

import { useReactiveVar } from "@apollo/client";
import { useCallback, useMemo } from "react";

import {
  type PipelineSuggestedPlayerFragmentFragment,
  useListPipelineSuggestedPlayersQuery,
} from "@/generated/graphql";

import {
  dismissSuggestion,
  dismissedSuggestionsVar,
} from "../stores/dismissedSuggestionsStore";

/** How many candidates the panel reads in one page. */
export const SUGGESTED_PLAYERS_PAGE_SIZE = 25;

/** A candidate as the panel consumes it. */
export type SuggestedCandidate = PipelineSuggestedPlayerFragmentFragment;

/** Public API of {@link useSuggestedPlayers}. */
export interface UseSuggestedPlayersResult {
  /** Candidates, strongest first, with session-dismissed entries removed. */
  readonly candidates: readonly SuggestedCandidate[];
  /** Whether the first page is still loading. */
  readonly isLoading: boolean;
  /** Human-facing read error, or `null`. */
  readonly error: string | null;
  /**
   * Hide a candidate for this pipeline and this session. Fires NO network
   * request of any kind.
   */
  readonly dismiss: (suggestionId: string) => void;
  /** Whether the connection reports more pages. */
  readonly hasMore: boolean;
  /** Fetch the next page. No-op when there is none. */
  readonly loadMore: () => void;
}

/**
 * Ascending-by-`bestRank` comparator. Ties keep their relative arrival order,
 * which `Array.prototype.sort` guarantees (stable since ES2019).
 * @param a - Left candidate.
 * @param b - Right candidate.
 * @returns Negative when `a` outranks `b`.
 */
const byBestRank = (a: SuggestedCandidate, b: SuggestedCandidate): number =>
  a.bestRank - b.bestRank;

/**
 * Read the Suggested tab's candidates for one pipeline.
 * @param kanbanBoardId - The open pipeline. Falsy while the board is resolving.
 * @param skip - Skip the read entirely (the tab is not selected / not offered).
 * @returns The {@link UseSuggestedPlayersResult} read model.
 */
const useSuggestedPlayers = (
  kanbanBoardId: string | null | undefined,
  skip = false
): UseSuggestedPlayersResult => {
  const { data, loading, error, fetchMore } =
    useListPipelineSuggestedPlayersQuery({
      variables: {
        kanbanBoardId: kanbanBoardId ?? "",
        first: SUGGESTED_PLAYERS_PAGE_SIZE,
      },
      // Gate on DATA readiness (no board id yet), not on UI visibility — the
      // panel body is remounted freely and a visibility-shaped guard would
      // re-fetch on every tab flip.
      skip: skip || !kanbanBoardId,
      // A snapshot refresh mid-session should be reflected without blanking the
      // list the user is looking at.
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
    });

  const dismissedByBoard = useReactiveVar(dismissedSuggestionsVar);

  const connection = data?.listPipelineSuggestedPlayers;

  const candidates = useMemo(() => {
    const dismissed = new Set(
      kanbanBoardId ? (dismissedByBoard[kanbanBoardId] ?? []) : []
    );
    return (connection?.edges ?? [])
      .map(edge => edge.node)
      .filter(node => !dismissed.has(node.id))
      .sort(byBestRank);
  }, [connection?.edges, dismissedByBoard, kanbanBoardId]);

  const dismiss = useCallback(
    (suggestionId: string) => {
      if (!kanbanBoardId) return;
      dismissSuggestion(kanbanBoardId, suggestionId);
    },
    [kanbanBoardId]
  );

  const hasMore = connection?.pageInfo.hasNextPage === true;
  const endCursor = connection?.pageInfo.endCursor ?? null;

  const loadMore = useCallback(() => {
    if (!hasMore || !endCursor || !kanbanBoardId) return;
    void fetchMore({
      variables: {
        kanbanBoardId,
        first: SUGGESTED_PLAYERS_PAGE_SIZE,
        after: endCursor,
      },
    });
  }, [endCursor, fetchMore, hasMore, kanbanBoardId]);

  return useMemo(
    () => ({
      candidates,
      isLoading: loading && candidates.length === 0,
      error: error?.message ?? null,
      dismiss,
      hasMore,
      loadMore,
    }),
    [candidates, dismiss, error?.message, hasMore, loadMore, loading]
  );
};

export default useSuggestedPlayers;
